// Selection primitives for single-scan edits. Pure functions over packed
// per-vertex arrays (world positions P, world normals N with NaN = none, source
// frames F); each returns a 0/1 mask the caller combines with the current
// selection. No WebGL here: lasso front-only takes a depth map the viewer read.
import type { M4 } from '../math.ts';
import { type Volume, MAX_VOLUMES, inBoxes, unitRows } from '../volumes.ts';
import { Grid } from '../align.ts';

export type Mask = Uint8Array;
export type Combine = 'replace' | 'add' | 'sub' | 'inter';

/** cur (op) m, into a new mask. */
export function combine(mode: Combine, cur: Mask, m: Mask): Mask {
  const out = new Uint8Array(m.length);
  for (let i = 0; i < m.length; i++)
    out[i] = mode === 'replace' ? m[i] : mode === 'add' ? cur[i] | m[i] : mode === 'sub' ? cur[i] & (m[i] ^ 1) : cur[i] & m[i];
  return out;
}

export const count = (m: Mask) => { let n = 0; for (let i = 0; i < m.length; i++) n += m[i]; return n; };

export const box = (P: Float32Array, vols: Volume[]): Mask => inBoxes(P, vols);

/** The view's cuts (viewer Settings): the volumes switched on. */
export interface Cuts {
  volumes: Volume[]; volShow: boolean;
}

/**
 * Points the view draws, as the point shader (shaders.ts) decides it: unless hidden points are
 * drawn tinted (volShow), those not in a hide volume that applies to this cloud and
 * outside every solid one.
 */
export function uncut(P: Float32Array, c: Cuts, cloud: string): Mask {
  const n = P.length / 3, m = new Uint8Array(n);
  const vols = c.volShow ? [] : c.volumes.slice(0, MAX_VOLUMES).filter(v => v.on && (!v.clouds.length || v.clouds.includes(cloud)));
  const hide = vols.filter(v => v.kind === 'hide').map(unitRows), solid = vols.filter(v => v.kind === 'solid').map(unitRows);
  const inAny = (rows: number[][], x: number, y: number, z: number) => rows.some(r =>
    Math.abs(r[0] * x + r[1] * y + r[2] * z + r[3]) <= 1 && Math.abs(r[4] * x + r[5] * y + r[6] * z + r[7]) <= 1 &&
    Math.abs(r[8] * x + r[9] * y + r[10] * z + r[11]) <= 1);
  for (let i = 0; i < n; i++) {
    const x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
    if (hide.length && inAny(hide, x, y, z) && !inAny(solid, x, y, z)) continue;
    m[i] = 1;
  }
  return m;
}

/**
 * Points not backface culled, as the point shader decides it (Settings.cull, cullT): no normal, or the
 * normal facing the eye (perspective) or against the view direction fwd (ortho) with cos of the angle >= t.
 */
export function facing(P: Float32Array, N: Float32Array, eye: readonly number[], fwd: readonly number[], ortho: boolean, t = 0): Mask {
  const n = P.length / 3, m = new Uint8Array(n), fl = Math.hypot(fwd[0], fwd[1], fwd[2]);
  for (let i = 0; i < n; i++) {
    const nx = N[3 * i], ny = N[3 * i + 1], nz = N[3 * i + 2];
    const vx = ortho ? -fwd[0] : eye[0] - P[3 * i], vy = ortho ? -fwd[1] : eye[1] - P[3 * i + 1], vz = ortho ? -fwd[2] : eye[2] - P[3 * i + 2];
    const d = (nx * vx + ny * vy + nz * vz) / (Math.hypot(nx, ny, nz) * (ortho ? fl : Math.hypot(vx, vy, vz)));
    m[i] = d < t ? 0 : 1;   // NaN (no normal) is kept
  }
  return m;
}

/** Points whose normal is within maxDeg of axis (or of -axis too, when twoSided: scan normals may be unoriented). */
export function normalCone(N: Float32Array, axis: [number, number, number], maxDeg: number, twoSided = true): Mask {
  const n = N.length / 3, m = new Uint8Array(n), l = Math.hypot(...axis) || 1;
  const ax = axis[0] / l, ay = axis[1] / l, az = axis[2] / l, c = Math.cos((maxDeg * Math.PI) / 180);
  for (let i = 0; i < n; i++) {
    const d = N[3 * i] * ax + N[3 * i + 1] * ay + N[3 * i + 2] * az;   // NaN (no normal) fails both tests
    m[i] = d >= c || (twoSided && -d >= c) ? 1 : 0;
  }
  return m;
}

/** Points with lo <= signed distance to plane (n·p + w, n unit) <= hi. */
export function band(P: Float32Array, plane: [number, number, number, number], lo: number, hi: number): Mask {
  const n = P.length / 3, m = new Uint8Array(n), l = Math.hypot(plane[0], plane[1], plane[2]) || 1;
  const [a, b, c, w] = plane.map(x => x / l);
  for (let i = 0; i < n; i++) {
    const d = a * P[3 * i] + b * P[3 * i + 1] + c * P[3 * i + 2] + w;
    m[i] = d >= lo && d <= hi ? 1 : 0;
  }
  return m;
}

/**
 * Points captured lo..hi m from their frame's camera, from the record's range byte (step m a unit, 0
 * unknown: never matches; 255 is that far and further, so it matches any hi from there up).
 */
export function distance(R: Uint8Array, step: number, lo: number, hi: number): Mask {
  const m = new Uint8Array(R.length), a = Math.max(1, Math.ceil(lo / step - 1e-6)), b = hi >= 255 * step - 1e-6 ? 255 : Math.floor(hi / step + 1e-6);
  for (let i = 0; i < R.length; i++) m[i] = R[i] >= a && R[i] <= b ? 1 : 0;
  return m;
}

/** The edge byte's signed Sobel strength in 31sts (prep/edges.py: bits 3-7, bit 2 the far side): -31 background to 31 foreground. */
export const edgeSigned = (e: number) => (e & 4 ? -(e >> 3) : e >> 3);

/**
 * Points by the edge byte (prep/edges.py: bit 0 known, bit 1 on a Canny edge, bit 2 on the far side of
 * the depth around it, bits 3-7 Sobel strength 0-31) of their pixel in the depth image they came from:
 * strength lo..hi (0-1, inclusive), or with signed the signed strength lo..hi (-1 background to 1
 * foreground: the far rim of a depth jump is the background, its near rim the foreground), and, with
 * canny true / false, on / off a Canny edge (null: either). Unknown (0) never matches. Signed needs bytes
 * with the side (an index with edgeSign); others' bit 2 is 0 (build_tiles.py) or, built before it, noise.
 */
export function edges(E: Uint8Array, lo: number, hi: number, canny: boolean | null, signed = false): Mask {
  const m = new Uint8Array(E.length), a = Math.ceil(lo * 31 - 1e-6), b = Math.floor(hi * 31 + 1e-6);
  for (let i = 0; i < E.length; i++) {
    const e = E[i], s = signed ? edgeSigned(e) : e >> 3;
    m[i] = e & 1 && s >= a && s <= b && (canny === null || ((e & 2) !== 0) === canny) ? 1 : 0;
  }
  return m;
}

/** Hue (degrees 0-360, NaN for a grey), saturation and value (0-1) of an 8-bit RGB colour. */
export function hsv(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b), d = mx - Math.min(r, g, b);
  if (!d) return [NaN, 0, mx / 255];
  const h = mx === r ? (g - b) / d : mx === g ? 2 + (b - r) / d : 4 + (r - g) / d;
  return [(h * 60 + 360) % 360, d / mx, mx / 255];
}

/** An HSV band: hue h0 to h1 in degrees, round through red when h0 > h1 (340 to 20); s and v 0-1, inclusive. */
export interface HsvBand { h0: number; h1: number; s0: number; s1: number; v0: number; v1: number }

/** True when a hue band takes in every hue (0 to 360 or wider), so greys, which have none, pass it. */
export const allHues = (b: HsvBand) => b.h1 - b.h0 >= 360 || (b.h0 <= 0 && b.h1 >= 360);

/**
 * Points whose colour (rgb, 3 bytes a point) is in the band. A grey has no hue: it matches only when the
 * hue band is every hue.
 */
export function colour(rgb: Uint8Array, b: HsvBand): Mask {
  const n = rgb.length / 3, m = new Uint8Array(n), any = allHues(b);
  const h0 = ((b.h0 % 360) + 360) % 360, h1 = ((b.h1 % 360) + 360) % 360, wrap = h0 > h1;
  for (let i = 0; i < n; i++) {
    const [h, s, v] = hsv(rgb[3 * i], rgb[3 * i + 1], rgb[3 * i + 2]);
    if (s < b.s0 || s > b.s1 || v < b.v0 || v > b.v1) continue;
    m[i] = any || (wrap ? h >= h0 || h <= h1 : h >= h0 && h <= h1) ? 1 : 0;   // NaN (grey) fails both
  }
  return m;
}

/**
 * The HSV band around the selected points' colours, `trim` of them left out at each end of each range
 * (stray points). The hue band is the arc the hues span, found by cutting the circle at the widest gap
 * between them; with fewer than a fifth of the points having a hue (s >= 0.05) it is every hue. Null: none selected.
 */
export function hsvBand(rgb: Uint8Array, mask: Mask, trim = 0.02): HsvBand | null {
  const H: number[] = [], S: number[] = [], V: number[] = [];
  for (let i = 0; i < mask.length; i++) if (mask[i]) {
    const [h, s, v] = hsv(rgb[3 * i], rgb[3 * i + 1], rgb[3 * i + 2]);
    S.push(s); V.push(v);
    if (s >= 0.05) H.push(h);
  }
  if (!S.length) return null;
  const q = (a: number[], f: number) => a[Math.min(a.length - 1, Math.max(0, Math.round(f * (a.length - 1))))];
  const range = (a: number[]): [number, number] => { a.sort((x, y) => x - y); return [q(a, trim), q(a, 1 - trim)]; };
  const [s0, s1] = range(S), [v0, v1] = range(V);
  let h0 = 0, h1 = 360;
  if (H.length >= S.length / 5) {
    H.sort((x, y) => x - y);
    let gap = H[0] + 360 - H[H.length - 1], at = 0;   // the gap through 0 / 360, ending at H[0]
    for (let i = 1; i < H.length; i++) if (H[i] - H[i - 1] > gap) { gap = H[i] - H[i - 1]; at = i; }
    const U = H.map((h, i) => (i < at ? h + 360 : h)).sort((x, y) => x - y);   // unwrapped, starting after the gap
    [h0, h1] = range(U);
    if (h1 - h0 >= 360) { h0 = 0; h1 = 360; } else { h0 %= 360; h1 %= 360; }
  }
  return { h0, h1, s0, s1, v0, v1 };
}

/** Points whose normal is within spread degrees of `at` degrees from the camera ray seen backwards (from the point
 *  to the eye; against fwd when ortho): at 0 the surface faces the camera head on, so at 0 spread 10 is a 10° cone
 *  round the ray, 90 is edge on, 180 faces away. No normal never matches. */
export function viewCone(P: Float32Array, N: Float32Array, eye: readonly number[], fwd: readonly number[], ortho: boolean,
  at: number, spread: number): Mask {
  const n = N.length / 3, m = new Uint8Array(n), fl = Math.hypot(fwd[0], fwd[1], fwd[2]) || 1;
  for (let i = 0; i < n; i++) {
    let vx = -fwd[0] / fl, vy = -fwd[1] / fl, vz = -fwd[2] / fl;
    if (!ortho) {
      vx = eye[0] - P[3 * i]; vy = eye[1] - P[3 * i + 1]; vz = eye[2] - P[3 * i + 2];
      const l = Math.hypot(vx, vy, vz) || 1; vx /= l; vy /= l; vz /= l;
    }
    const nx = N[3 * i], ny = N[3 * i + 1], nz = N[3 * i + 2];
    const c = (nx * vx + ny * vy + nz * vz) / Math.hypot(nx, ny, nz);   // NaN (no normal, or a zero one) fails the test
    const a = (Math.acos(Math.max(-1, Math.min(1, c))) * 180) / Math.PI;
    m[i] = Math.abs(a - at) <= spread ? 1 : 0;
  }
  return m;
}

/** Points of known frames (0 = unknown never matches) that have fewer than n named points (count: per frame, 0 when absent). */
export function fewPoints(F: Uint32Array, count: Map<number, number>, n: number): Mask {
  const m = new Uint8Array(F.length);
  for (let i = 0; i < F.length; i++) m[i] = F[i] && (count.get(F[i]) ?? 0) < n ? 1 : 0;
  return m;
}

/** Points whose source frame lies in one of the inclusive ranges (0 = unknown frame never matches). */
export function frames(F: Uint32Array, ranges: [number, number][]): Mask {
  const m = new Uint8Array(F.length);
  for (let i = 0; i < F.length; i++) {
    const f = F[i];
    if (f) for (const [a, b] of ranges) if (f >= a && f <= b) { m[i] = 1; break; }
  }
  return m;
}

/** The mask widened to every point of a frame it touches (0 = unknown frame: those points stay as they are). */
export function widenToFrames(m: Mask, F: Uint32Array): Mask {
  const hit = new Set<number>();
  for (let i = 0; i < m.length; i++) if (m[i] && F[i]) hit.add(F[i]);
  const out = new Uint8Array(m.length);
  for (let i = 0; i < m.length; i++) out[i] = m[i] | (hit.has(F[i]) ? 1 : 0);
  return out;
}

export interface DepthMap { x: number; y: number; w: number; h: number; d: Float32Array }

/**
 * Points that project inside the screen polygon (CSS px, even-odd rule). With
 * a depth map (the front surface's view depth over the polygon's bounds),
 * only points no further than the nearest depth in their 3x3 pixel
 * neighbourhood plus tol(depth) are kept, i.e. the visible ones.
 */
export function lasso(P: Float32Array, vp: M4, view: M4, w: number, h: number, poly: [number, number][],
  depth?: DepthMap | null, tol: (d: number) => number = d => Math.max(0.02, 0.01 * d)): Mask {
  const n = P.length / 3, m = new Uint8Array(n);
  if (poly.length < 3) return m;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of poly) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  const M = vp, V = view;
  for (let i = 0; i < n; i++) {
    const px = P[3 * i], py = P[3 * i + 1], pz = P[3 * i + 2];
    const cw = M[3] * px + M[7] * py + M[11] * pz + M[15];
    if (cw <= 0) continue;
    const cx = (M[0] * px + M[4] * py + M[8] * pz + M[12]) / cw, cy = (M[1] * px + M[5] * py + M[9] * pz + M[13]) / cw;
    const cz = (M[2] * px + M[6] * py + M[10] * pz + M[14]) / cw;
    if (cz < -1 || cz > 1) continue;
    const sx = ((cx + 1) / 2) * w, sy = ((1 - cy) / 2) * h;
    if (sx < x0 || sx > x1 || sy < y0 || sy > y1 || !inPoly(poly, sx, sy)) continue;
    if (depth) {
      const vz = -(V[2] * px + V[6] * py + V[10] * pz + V[14]);   // view depth
      const near = nearestDepth(depth, sx, sy);
      if (near > 0 && vz > near + tol(near)) continue;
    }
    m[i] = 1;
  }
  return m;
}

function nearestDepth(dm: DepthMap, sx: number, sy: number) {
  const ix = Math.floor(sx) - dm.x, iy = Math.floor(sy) - dm.y;
  let best = 0;
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
    const x = ix + i, y = iy + j;
    if (x < 0 || y < 0 || x >= dm.w || y >= dm.h) continue;
    const d = dm.d[y * dm.w + x];
    if (d > 0 && (best === 0 || d < best)) best = d;
  }
  return best;
}

export function inPoly(poly: [number, number][], x: number, y: number) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** A circle as a polygon (the brush stamps these). */
export const circle = (x: number, y: number, r: number, k = 20): [number, number][] =>
  Array.from({ length: k }, (_, i) => [x + r * Math.cos((2 * Math.PI * i) / k), y + r * Math.sin((2 * Math.PI * i) / k)]);

/**
 * Region growing from seed vertices: repeatedly add points within radius of a
 * member whose normal is within maxDeg of the member's (ref 'neighbour') or of
 * the seed's (ref 'seed'), among allowed points. Points without a normal join
 * on distance alone. With color, a point also needs its colour within color.tol
 * (RGB distance, 0-441) of the seed's or of the member it grows from. A generator
 * so the caller can time-slice it; yields the number added so far, returns the mask.
 */
export function* grow(P: Float32Array, N: Float32Array, seeds: number[], radius: number, maxDeg: number,
  opts: { ref?: 'seed' | 'neighbour'; allowed?: Mask | null; grid?: Grid; cap?: number;
    color?: { rgb: Uint8Array; tol: number; ref: 'seed' | 'neighbour'; seedRgb?: number[] } } = {}): Generator<number, Mask> {
  const n = P.length / 3, m = new Uint8Array(n), grid = opts.grid ?? new Grid(P, radius);
  const c = Math.cos((maxDeg * Math.PI) / 180), allowed = opts.allowed, cap = opts.cap ?? Infinity;
  const queue: number[] = [];
  for (const s of seeds) if (s >= 0 && s < n && !m[s] && (!allowed || allowed[s])) { m[s] = 1; queue.push(s); }
  const sn = seeds.length ? [N[3 * seeds[0]], N[3 * seeds[0] + 1], N[3 * seeds[0] + 2]] : [NaN, NaN, NaN];
  const col = opts.color, tol2 = col ? col.tol * col.tol : 0, s0 = seeds.length ? seeds[0] : 0;
  let added = queue.length, steps = 0;
  for (let qi = 0; qi < queue.length && added < cap; qi++) {
    const a = queue[qi];
    const rx = opts.ref === 'seed' ? sn[0] : N[3 * a], ry = opts.ref === 'seed' ? sn[1] : N[3 * a + 1], rz = opts.ref === 'seed' ? sn[2] : N[3 * a + 2];
    const refOk = rx === rx;   // not NaN
    const C = col?.rgb, ref = col ? (col.ref === 'seed' ? col.seedRgb ?? [C![3 * s0], C![3 * s0 + 1], C![3 * s0 + 2]] : [C![3 * a], C![3 * a + 1], C![3 * a + 2]]) : null;
    grid.within(P[3 * a], P[3 * a + 1], P[3 * a + 2], radius, b => {
      if (m[b] || (allowed && !allowed[b])) return;
      if (ref) {
        const dr = C![3 * b] - ref[0], dg = C![3 * b + 1] - ref[1], db = C![3 * b + 2] - ref[2];
        if (dr * dr + dg * dg + db * db > tol2) return;
      }
      if (refOk) {
        const bx = N[3 * b];
        if (bx === bx && Math.abs(bx * rx + N[3 * b + 1] * ry + N[3 * b + 2] * rz) < c) return;
      }
      m[b] = 1; queue.push(b); added++;
    });
    if (++steps % 2000 === 0) yield added;
  }
  return m;
}

/** One cloud for growMany: world points, normals, colours, which points may join, and a grid over P. */
export interface GrowCloud { P: Float32Array; N: Float32Array; rgb: Uint8Array; allowed: Mask | null; grid: Grid }

/**
 * grow() across several clouds (scans) that overlap: grow in one, hand over to the
 * other clouds' points within radius of what joined (same normal / colour tests,
 * against the point they join from), grow there, and so on until nothing joins.
 * Yields the number added so far; returns a mask per cloud.
 */
export function* growMany(clouds: GrowCloud[], from: number, seed: number, radius: number, maxDeg: number,
  color?: { tol: number; ref: 'seed' | 'neighbour' }): Generator<number, Mask[]> {
  const masks = clouds.map(c => new Uint8Array(c.P.length / 3)), cmin = Math.cos((maxDeg * Math.PI) / 180);
  const S = clouds[from].rgb, seedRgb = [S[3 * seed], S[3 * seed + 1], S[3 * seed + 2]], tol2 = color ? color.tol ** 2 : 0;
  let pending: number[][] = clouds.map((_, k) => (k === from ? [seed] : [])), total = 0, steps = 0;
  for (let round = 0; pending.some(p => p.length) && round < 1000; round++) {
    const fresh: number[][] = clouds.map(() => []);
    for (let k = 0; k < clouds.length; k++) {
      if (!pending[k].length) continue;
      const c = clouds[k], n = masks[k].length, allow = new Uint8Array(n);
      for (let i = 0; i < n; i++) allow[i] = (!c.allowed || c.allowed[i]) && !masks[k][i] ? 1 : 0;
      const g = grow(c.P, c.N, pending[k], radius, maxDeg, {
        allowed: allow, grid: c.grid, color: color && { rgb: c.rgb, tol: color.tol, ref: color.ref, seedRgb } });
      let r = g.next();
      for (; !r.done; r = g.next()) yield total + r.value;
      const m = r.value;
      for (let i = 0; i < n; i++) if (m[i] && !masks[k][i]) { masks[k][i] = 1; fresh[k].push(i); total++; }
    }
    pending = clouds.map(() => []);
    const queued = clouds.map(c => new Uint8Array(c.P.length / 3));
    for (let a = 0; a < clouds.length; a++) {
      const A = clouds[a];
      for (const i of fresh[a]) {
        const nx = A.N[3 * i], ny = A.N[3 * i + 1], nz = A.N[3 * i + 2];
        const ref = color && (color.ref === 'seed' ? seedRgb : [A.rgb[3 * i], A.rgb[3 * i + 1], A.rgb[3 * i + 2]]);
        for (let b = 0; b < clouds.length; b++) {
          if (b === a) continue;
          const B = clouds[b];
          B.grid.within(A.P[3 * i], A.P[3 * i + 1], A.P[3 * i + 2], radius, j => {
            if (masks[b][j] || queued[b][j] || (B.allowed && !B.allowed[j])) return;
            const bx = B.N[3 * j];
            if (nx === nx && bx === bx && Math.abs(bx * nx + B.N[3 * j + 1] * ny + B.N[3 * j + 2] * nz) < cmin) return;
            if (ref) {
              const dr = B.rgb[3 * j] - ref[0], dg = B.rgb[3 * j + 1] - ref[1], db = B.rgb[3 * j + 2] - ref[2];
              if (dr * dr + dg * dg + db * db > tol2) return;
            }
            queued[b][j] = 1; pending[b].push(j);
          });
        }
        if (++steps % 2000 === 0) yield total;
      }
    }
  }
  return masks;
}

/** One cloud for near(): world points, the points searched from (src) and the ones that may match (dst). */
export interface NearCloud { P: Float32Array; src: Mask; dst: Mask }

/** Non-negative int keys to dense ids 0, 1, 2… in order of first add (open addressing: far quicker than a Map here). */
class Ids {
  n = 0;
  keys = new Int32Array(16);
  private slot = new Int32Array(32).fill(-1);
  private bits = 5;
  private at(k: number) {
    const mask = this.slot.length - 1;
    let h = Math.imul(k, 0x9e3779b1) >>> (32 - this.bits);
    while (this.slot[h] >= 0 && this.keys[this.slot[h]] !== k) h = (h + 1) & mask;
    return h;
  }
  private lastK = -1;
  private lastId = -1;
  get(k: number) { return this.slot[this.at(k)]; }
  add(k: number) {
    if (k === this.lastK) return this.lastId;   // points in scan order mostly fall in the cell before's
    this.lastK = k;
    return (this.lastId = this.put(k));
  }
  private put(k: number) {
    let h = this.at(k);
    if (this.slot[h] >= 0) return this.slot[h];
    if (2 * (this.n + 1) > this.slot.length) {
      this.bits++;
      this.slot = new Int32Array(1 << this.bits).fill(-1);
      const keys = new Int32Array(2 * this.keys.length); keys.set(this.keys); this.keys = keys;
      for (let i = 0; i < this.n; i++) this.slot[this.at(this.keys[i])] = i;
      h = this.at(k);
    } else if (this.n === this.keys.length) { const keys = new Int32Array(2 * this.n); keys.set(this.keys); this.keys = keys; }
    this.keys[this.n] = k;
    return (this.slot[h] = this.n++);
  }
}

/**
 * Per cloud, the dst points within r of a src point of any cloud (scans overlap). Dilate is dst: unselected,
 * src: selected; erode the other way round. Coarse cells (side r, 10 cm at least; a byte each) keep only the
 * points near where the two meet; there, both go into fine cells (side r / 2), and a dst cell looks at the src
 * cells within reach once for all its points. Per point, a src cell wholly within r (or wholly out of it)
 * answers from its box; only the ones the sphere cuts need their points' distances.
 */
export function near(clouds: NearCloud[], r: number): Mask[] {
  const out = clouds.map(o => new Uint8Array(o.dst.length));
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (const o of clouds) for (let i = 0; i < o.dst.length; i++) if (o.src[i] || o.dst[i]) for (let k = 0; k < 3; k++) {
    const v = o.P[3 * i + k];
    if (v < lo[k]) lo[k] = v;
    if (v > hi[k]) hi[k] = v;
  }
  if (!(lo[0] <= hi[0] && hi.every(Number.isFinite) && lo.every(Number.isFinite))) return out;
  // fine cells (with a border of a reach) under 2^28: a bit each says it holds src; coarse ones a byte each
  let f = r / 2, c = Math.max(r, 0.1);
  const B = () => Math.ceil(r / f), dims = (w: number, b: number) => hi.map((h, k) => Math.floor((h - lo[k]) / w) + 1 + 2 * b);
  const vol = (d: number[]) => d[0] * d[1] * d[2];
  while (vol(dims(f, B())) > 2 ** 28) f *= 1.25;
  c = Math.max(c, 2 * f);
  while (vol(dims(c, 1)) > 2 ** 25) c *= 1.25;
  const K = B(), [nx, ny, nz] = dims(c, 1), FD = dims(f, K), [FX, FY] = FD;
  const ck = (P: Float32Array, i: number) => (Math.floor((P[3 * i] - lo[0]) / c) + 1) + nx * ((Math.floor((P[3 * i + 1] - lo[1]) / c) + 1) + ny * (Math.floor((P[3 * i + 2] - lo[2]) / c) + 1));
  const fk = (P: Float32Array, i: number) => (Math.floor((P[3 * i] - lo[0]) / f) + K) + FX * ((Math.floor((P[3 * i + 1] - lo[1]) / f) + K) + FY * (Math.floor((P[3 * i + 2] - lo[2]) / f) + K));
  // coarse: 1 holds src, 2 holds dst; then 4: its src points may reach a dst, 8: its dst points may be reached
  const flag = new Uint8Array(nx * ny * nz), used: number[] = [], cks = clouds.map(o => new Int32Array(o.dst.length).fill(-1));
  clouds.forEach((o, n) => {
    for (let i = 0; i < o.dst.length; i++) {
      const b = (o.src[i] ? 1 : 0) | (o.dst[i] ? 2 : 0);
      if (!b) continue;
      const k = ck(o.P, i);
      if (!(k >= 0 && k < flag.length)) continue;   // no position (NaN): takes no part
      if (!flag[k]) used.push(k);
      flag[k] |= b; cks[n][i] = k;
    }
  });
  for (const k of used) {
    let around = 0;
    for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) around |= flag[k + x + nx * (y + ny * z)];
    const b = flag[k];
    flag[k] |= (b & 1 && around & 2 ? 4 : 0) | (b & 2 && around & 1 ? 8 : 0);
  }
  // the points that take part, with their fine cells: src (as xyz in S) and dst (as cloud, index in D), by cell
  const sc = new Ids(), dc = new Ids(), sp: number[] = [], dp: number[] = [];   // cloud, index, cell id, …
  clouds.forEach((o, n) => {
    const ks = cks[n];
    for (let i = 0; i < ks.length; i++) {
      const k = ks[i];
      if (k < 0) continue;
      if (o.src[i] && flag[k] & 4) sp.push(n, i, sc.add(fk(o.P, i)));
      if (o.dst[i] && flag[k] & 8) dp.push(n, i, dc.add(fk(o.P, i)));
    }
  });
  if (!sc.n || !dc.n) return out;
  const sat = new Int32Array(sc.n + 1), dat = new Int32Array(dc.n + 1);
  for (let t = 2; t < sp.length; t += 3) sat[sp[t] + 1]++;
  for (let t = 2; t < dp.length; t += 3) dat[dp[t] + 1]++;
  for (let i = 0; i < sc.n; i++) sat[i + 1] += sat[i];
  for (let i = 0; i < dc.n; i++) dat[i + 1] += dat[i];
  const S = new Float32Array(sp.length), D = new Int32Array((2 * dp.length) / 3), sfill = sat.slice(0, sc.n), dfill = dat.slice(0, dc.n);
  for (let t = 0; t < sp.length; t += 3) {
    const P = clouds[sp[t]].P, i = sp[t + 1], j = sfill[sp[t + 2]]++;
    S[3 * j] = P[3 * i]; S[3 * j + 1] = P[3 * i + 1]; S[3 * j + 2] = P[3 * i + 2];
  }
  for (let t = 0; t < dp.length; t += 3) { const j = dfill[dp[t + 2]]++; D[2 * j] = dp[t]; D[2 * j + 1] = dp[t + 1]; }
  const has = new Uint32Array(Math.ceil(vol(FD) / 32));
  for (let s = 0; s < sc.n; s++) has[sc.keys[s] >>> 5] |= 1 << (sc.keys[s] & 31);
  // the fine cells within reach of a cell, nearest first; whole: every point of it is within r of every point of ours
  const offs: { d: number; whole: boolean; near2: number }[] = [], r2 = r * r;
  for (let x = -K; x <= K; x++) for (let y = -K; y <= K; y++) for (let z = -K; z <= K; z++) {
    const near2 = f * f * [x, y, z].reduce((a, v) => a + Math.max(Math.abs(v) - 1, 0) ** 2, 0);
    const far2 = f * f * [x, y, z].reduce((a, v) => a + (Math.abs(v) + 1) ** 2, 0);
    if (near2 <= r2 * (1 + 1e-6)) offs.push({ d: x + FX * (y + FY * z), whole: far2 <= r2 * (1 - 1e-6), near2 });
  }
  offs.sort((a, b) => +b.whole - +a.whole || a.near2 - b.near2);
  // dst cells in key order (neighbouring cells' bits are near each other), each with the src cells it can reach
  const order = new Float64Array(dc.n);
  for (let g = 0; g < dc.n; g++) order[g] = dc.keys[g] * 2 ** 25 + g;   // keys < 2^28: exact in a double
  order.sort();
  const edge: number[] = [];
  for (const o of order) {
    const g = o % 2 ** 25, k = dc.keys[g], d0 = dat[g], d1 = dat[g + 1];
    let all = false;
    edge.length = 0;
    for (const { d, whole } of offs) {
      const e = k + d;
      if (!(has[e >>> 5] & (1 << (e & 31)))) continue;
      if (whole) { all = true; break; }
      edge.push(sc.get(e));
    }
    if (all) { for (let t = d0; t < d1; t++) out[D[2 * t]][D[2 * t + 1]] = 1; continue; }
    if (!edge.length) continue;
    for (let t = d0; t < d1; t++) {
      const P = clouds[D[2 * t]].P, i = D[2 * t + 1], x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
      let hit = false;
      for (const s of edge) {
        // the cell's box: out of reach, wholly in reach, or its points one by one
        const e = sc.keys[s];
        const x0 = lo[0] + ((e % FX) - K) * f, y0 = lo[1] + ((Math.floor(e / FX) % FY) - K) * f, z0 = lo[2] + (Math.floor(e / (FX * FY)) - K) * f;
        const nx2 = Math.max(x0 - x, 0, x - x0 - f) ** 2 + Math.max(y0 - y, 0, y - y0 - f) ** 2 + Math.max(z0 - z, 0, z - z0 - f) ** 2;
        if (nx2 > r2 * (1 + 1e-6)) continue;
        const fx2 = Math.max(x - x0, x0 + f - x) ** 2 + Math.max(y - y0, y0 + f - y) ** 2 + Math.max(z - z0, z0 + f - z) ** 2;
        if (fx2 <= r2 * (1 - 1e-6)) { hit = true; break; }
        for (let j = sat[s]; j < sat[s + 1] && !hit; j++) hit = (S[3 * j] - x) ** 2 + (S[3 * j + 1] - y) ** 2 + (S[3 * j + 2] - z) ** 2 <= r2;
        if (hit) break;
      }
      if (hit) out[D[2 * t]][i] = 1;
    }
  }
  return out;
}

/** Run a generator to the end (tests, small inputs). */
export function finish<T>(g: Generator<unknown, T>): T {
  for (;;) { const r = g.next(); if (r.done) return r.value; }
}

/**
 * Least-squares plane through the masked points: [nx, ny, nz, w] with n unit
 * (n·p + w = 0), n pointing up (+z) when not vertical. null if under 3 points.
 */
export function fitPlane(P: Float32Array, m: Mask): [number, number, number, number] | null {
  let k = 0, cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < m.length; i++) if (m[i]) { cx += P[3 * i]; cy += P[3 * i + 1]; cz += P[3 * i + 2]; k++; }
  if (k < 3) return null;
  cx /= k; cy /= k; cz /= k;
  const C = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let i = 0; i < m.length; i++) if (m[i]) {
    const d = [P[3 * i] - cx, P[3 * i + 1] - cy, P[3 * i + 2] - cz];
    for (let r = 0; r < 3; r++) for (let s = 0; s < 3; s++) C[3 * r + s] += d[r] * d[s];
  }
  const { vals, vecs } = eigSym3(C);
  let j = 0;
  for (let t = 1; t < 3; t++) if (vals[t] < vals[j]) j = t;
  let n = [vecs[j], vecs[3 + j], vecs[6 + j]];
  if (n[2] < 0 || (Math.abs(n[2]) < 1e-9 && (n[1] < 0 || (n[1] === 0 && n[0] < 0)))) n = n.map(x => -x);
  return [n[0], n[1], n[2], -(n[0] * cx + n[1] * cy + n[2] * cz)];
}

/** Eigen-decomposition of a symmetric 3x3 (row-major) by Jacobi rotations; vecs' columns are the eigenvectors. */
export function eigSym3(A0: number[]) {
  const A = [...A0], V = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  for (let sweep = 0; sweep < 50; sweep++) {
    const off = A[1] ** 2 + A[2] ** 2 + A[5] ** 2;
    if (off < 1e-30) break;
    for (const [p, q] of [[0, 1], [0, 2], [1, 2]]) {
      const apq = A[3 * p + q];
      if (Math.abs(apq) < 1e-300) continue;
      const th = (A[3 * q + q] - A[3 * p + p]) / (2 * apq);
      const t = Math.sign(th || 1) / (Math.abs(th) + Math.sqrt(th * th + 1)), c = 1 / Math.sqrt(t * t + 1), s = t * c;
      for (let k = 0; k < 3; k++) {   // A = A J
        const akp = A[3 * k + p], akq = A[3 * k + q];
        A[3 * k + p] = c * akp - s * akq; A[3 * k + q] = s * akp + c * akq;
      }
      for (let k = 0; k < 3; k++) {   // A = J^T A
        const apk = A[3 * p + k], aqk = A[3 * q + k];
        A[3 * p + k] = c * apk - s * aqk; A[3 * q + k] = s * apk + c * aqk;
      }
      for (let k = 0; k < 3; k++) {
        const vkp = V[3 * k + p], vkq = V[3 * k + q];
        V[3 * k + p] = c * vkp - s * vkq; V[3 * k + q] = s * vkp + c * vkq;
      }
    }
  }
  return { vals: [A[0], A[4], A[8]], vecs: V };
}

/** Histogram of known frames: sorted distinct frame ids with their point counts. */
export function frameHistogram(F: Uint32Array, mask?: Mask | null): { frame: number; n: number }[] {
  const h = new Map<number, number>();
  for (let i = 0; i < F.length; i++) if (F[i] && (!mask || mask[i])) h.set(F[i], (h.get(F[i]) ?? 0) + 1);
  return [...h.entries()].sort((a, b) => a[0] - b[0]).map(([frame, n]) => ({ frame, n }));
}
